import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './LoginPage.css';
import { useBrand } from '../context/BrandContext';
const API_URL  = process.env.REACT_APP_API_URL || '';
const isValidPAN = p => /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(p);
const stripHtml = text => String(text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const friendlyErrorMessage = (status, rawMessage) => {
  const normalized = stripHtml(rawMessage);
  const lower = normalized.toLowerCase();

  if (!normalized) {
    return status >= 500
      ? 'Our server is temporarily unavailable. Please try again in a moment.'
      : `Request failed with status ${status}`;
  }

  if (
    lower.includes('bad gateway') ||
    lower.includes('gateway timeout') ||
    lower.includes('service unavailable') ||
    lower.includes('nginx')
  ) {
    return 'Server is temporarily unavailable. Please try again shortly.';
  }

  return normalized;
};

/* ── Crisp validation check ────────────────────────────── */
function CheckIcon() {
  return (
    <span className="lp-check">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

/* ── Trust strip (reassurance for a lending login) ─────── */
function TrustStrip() {
  const items = [
    {
      label: 'Bank-grade security',
      icon: <path d="M12 2 4 5v6c0 5.3 3.4 9.7 8 11 4.6-1.3 8-5.7 8-11V5z" />,
    },
    {
      label: 'No paperwork',
      icon: <path d="M13 2 4 14h6l-1 8 9-12h-6z" />,
    },
    {
      label: 'Your data is safe',
      icon: <path d="M7 10V7a5 5 0 0 1 10 0v3h.5A1.5 1.5 0 0 1 19 11.5v8a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19.5v-8A1.5 1.5 0 0 1 6.5 10zm2 0h6V7a3 3 0 0 0-6 0z" />,
    },
  ];
  return (
    <div className="lp-trust">
      {items.map((it, i) => (
        <span className="lp-trust-item" key={i}>
          <span className="lp-trust-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              {it.icon}
            </svg>
          </span>
          <span>{it.label}</span>
        </span>
      ))}
    </div>
  );
}

/* ── Desktop side column: perks + illustration ────────────────── */
const HERO_PERKS = [
  {
    tone: 'blue',
    title: 'Secure',
    text: 'Bank-grade security',
    icon: <path d="M12 2 4 5v6c0 5.3 3.4 9.7 8 11 4.6-1.3 8-5.7 8-11V5z" />,
  },
  {
    tone: 'violet',
    title: 'Fast',
    text: 'Quick & easy access',
    icon: <path d="M13 2 4 14h6l-1 8 9-12h-6z" />,
  },
  {
    tone: 'green',
    title: 'Trusted',
    text: 'Built for financial growth',
    icon: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 19c.6-3.6 3.2-5.5 6.5-5.5s5.9 1.9 6.5 5.5z" /><circle cx="17" cy="9" r="2.6" /><path d="M16.8 13.2c2.6.1 4.3 1.7 4.8 4.8h-4.2a8 8 0 0 0-2.3-4.3 6 6 0 0 1 1.7-.5z" /></>,
  },
];

function LoginArt() {
  return (
    <svg className="lpd-art" viewBox="0 0 560 330" preserveAspectRatio="xMinYMid meet" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="lpArtScreen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#e3ecff" />
        </linearGradient>
        <linearGradient id="lpArtBase" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dde7ff" />
          <stop offset="100%" stopColor="#b7cbf9" />
        </linearGradient>
        <linearGradient id="lpArtBlue" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#5b9bff" />
          <stop offset="100%" stopColor="#1d4ed8" />
        </linearGradient>
        <linearGradient id="lpArtLeaf" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6fe0b0" />
          <stop offset="100%" stopColor="#1fa97a" />
        </linearGradient>
        <filter id="lpArtBlur" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="10" />
        </filter>
      </defs>

      {/* backdrop */}
      <path d="M96 150C96 78 170 30 262 34c70 3 112-14 170 18 62 34 96 96 78 160-16 58-84 86-170 90-96 5-244 4-244-152z" fill="#dce7ff" opacity=".75" />
      <ellipse cx="300" cy="302" rx="215" ry="15" fill="#9db8f2" opacity=".45" filter="url(#lpArtBlur)" />

      {/* plant */}
      <g transform="translate(96 250)">
        <path d="M0 4C-6-30-34-44-44-70c26 2 46 24 44 74z" fill="url(#lpArtLeaf)" />
        <path d="M2 4C6-34 30-54 46-84c8 30-6 66-44 88z" fill="#34c08a" />
        <path d="M0 4c-10-36-4-70 6-96 12 28 12 62-6 96z" fill="#5fd6a4" />
        <path d="M-26 0h52l-5 38a8 8 0 0 1-8 7h-26a8 8 0 0 1-8-7z" fill="#ffffff" stroke="#e3ebfb" strokeWidth="1.5" />
      </g>

      {/* laptop */}
      <path d="M152 258 406 224 494 258 240 296z" fill="url(#lpArtBase)" stroke="url(#lpArtBase)" strokeWidth="12" strokeLinejoin="round" />
      <path d="M292 268 372 257 398 267 318 279z" fill="#ffffff" opacity=".55" />
      <g transform="translate(150 62) skewY(-7.5)">
        <rect width="258" height="190" rx="16" fill="url(#lpArtScreen)" stroke="#ffffff" strokeWidth="3" />
        <circle cx="18" cy="16" r="3" fill="#c9d8fb" />
        <circle cx="28" cy="16" r="3" fill="#c9d8fb" />
        <rect x="38" y="13" width="30" height="6" rx="3" fill="#dbe5fb" />
        <rect x="14" y="32" width="230" height="144" rx="10" fill="#f5f8ff" />
        <circle cx="46" cy="66" r="17" fill="#ffffff" stroke="#cfdcfb" strokeWidth="1.5" />
        <circle cx="46" cy="61" r="6" fill="#2f6bf0" />
        <path d="M35 77a11 9 0 0 1 22 0z" fill="#2f6bf0" />
        <rect x="76" y="54" width="92" height="8" rx="4" fill="#cddafc" />
        <rect x="180" y="54" width="34" height="8" rx="4" fill="#dde6fc" />
        <rect x="76" y="70" width="60" height="8" rx="4" fill="#dde6fc" />
        <rect x="28" y="108" width="70" height="8" rx="4" fill="#dde6fc" />
        <rect x="28" y="124" width="84" height="8" rx="4" fill="#dde6fc" />
        <rect x="28" y="140" width="50" height="8" rx="4" fill="#dde6fc" />
        <rect x="128" y="96" width="100" height="68" rx="8" fill="#ffffff" stroke="#e3ebfb" strokeWidth="1.5" />
        <rect x="142" y="136" width="12" height="18" rx="3" fill="url(#lpArtBlue)" />
        <rect x="160" y="126" width="12" height="28" rx="3" fill="url(#lpArtBlue)" />
        <rect x="178" y="116" width="12" height="38" rx="3" fill="url(#lpArtBlue)" />
        <rect x="196" y="106" width="12" height="48" rx="3" fill="url(#lpArtBlue)" />
      </g>

      {/* coins */}
      <g transform="translate(488 270)">
        {[14, 0, -14].map(y => (
          <g key={y} transform={`translate(0 ${y})`}>
            <path d="M-28 0v9c0 6 12.5 10 28 10s28-4 28-10V0z" fill="#2f63e6" />
            <ellipse rx="28" ry="10" fill="#5b8dff" />
          </g>
        ))}
      </g>

      {/* shield */}
      <ellipse cx="420" cy="262" rx="44" ry="8" fill="#5f86de" opacity=".4" filter="url(#lpArtBlur)" />
      <g transform="translate(420 176)">
        <g className="lpd-art-shield">
          <path d="M0-66C20-52 40-47 56-47V4c0 34-26 54-56 68-30-14-56-34-56-68v-51c16 0 36-5 56-19z" fill="url(#lpArtBlue)" />
          <path d="M0-66C20-52 40-47 56-47V4c0 34-26 54-56 68-30-14-56-34-56-68v-51c16 0 36-5 56-19z" transform="scale(.84)" stroke="#ffffff" strokeOpacity=".4" strokeWidth="2.5" />
          <rect x="-17" y="-6" width="34" height="30" rx="6" fill="#ffffff" />
          <path d="M-10-6v-8a10 10 0 0 1 20 0v8" stroke="#ffffff" strokeWidth="5" strokeLinecap="round" />
          <circle cx="0" cy="6" r="3.5" fill="#2563eb" />
          <rect x="-1.5" y="7" width="3" height="8" rx="1.5" fill="#2563eb" />
        </g>
      </g>
    </svg>
  );
}

/* ── Header illustration: shield + lock ────────────────── */
function ShieldArt() {
  const shield = 'M0-66C20-52 40-47 56-47V4c0 34-26 54-56 68-30-14-56-34-56-68v-51c16 0 36-5 56-19z';
  return (
    <svg className="lp-art" viewBox="0 0 240 220" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="lpArtBlob" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffffff" stopOpacity=".62" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity=".2" />
        </linearGradient>
        <linearGradient id="lpArtShield" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4d9bff" />
          <stop offset="100%" stopColor="#1557d8" />
        </linearGradient>
        <linearGradient id="lpArtLock" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#dce9ff" />
        </linearGradient>
      </defs>

      {/* glossy backdrop */}
      <path d="M52 92c0-46 34-78 80-74 40 3 74 26 80 66 5 34 22 44 16 78-6 30-36 46-70 44-22-1-30 8-56 2-38-8-50-62-50-116z" fill="url(#lpArtBlob)" />
      <circle cx="190" cy="170" r="34" fill="#ffffff" opacity=".34" />
      <ellipse cx="120" cy="124" rx="112" ry="30" transform="rotate(-10 120 124)" stroke="#ffffff" strokeOpacity=".6" strokeWidth="2.5" />
      <circle cx="196" cy="22" r="8" fill="#ffffff" opacity=".8" />
      <circle cx="170" cy="14" r="4" fill="#ffffff" opacity=".6" />
      <circle cx="20" cy="104" r="5" fill="#ffffff" opacity=".75" />
      <circle cx="52" cy="186" r="5" fill="#ffffff" opacity=".6" />

      {/* shield */}
      <g transform="translate(122 110)">
        <g className="lp-art-shield">
          <path d={shield} transform="scale(1.22)" fill="#7db4ff" />
          <path d={shield} transform="scale(1.1)" fill="url(#lpArtShield)" />
          <path d="M0-66c-20 14-40 19-56 19V4c0 34 26 54 56 68z" transform="scale(1.1)" fill="#ffffff" opacity=".13" />
          <path d={shield} transform="scale(.9)" stroke="#ffffff" strokeOpacity=".28" strokeWidth="2.5" />
          {/* clay gloss + underside shade */}
          <path d="M-44-40c12-3 24-8 36-16" stroke="#ffffff" strokeOpacity=".6" strokeWidth="7" strokeLinecap="round" />
          <path d="M-40 34c10 16 24 26 40 34 16-8 30-18 40-34" stroke="#0b3daa" strokeOpacity=".3" strokeWidth="8" strokeLinecap="round" />
          <path d="M-13-8v-10a13 13 0 0 1 26 0v10" stroke="url(#lpArtLock)" strokeWidth="7" strokeLinecap="round" />
          <rect x="-23" y="-8" width="46" height="38" rx="8" fill="url(#lpArtLock)" />
          <circle cx="0" cy="7" r="4.5" fill="#1d5fe0" />
          <rect x="-2" y="8" width="4" height="11" rx="2" fill="#1d5fe0" />
        </g>
      </g>
    </svg>
  );
}

/* ── Alert ─────────────────────────────────────────────── */
function Alert({ type, msg }) {
  if (!msg) return null;
  return (
    <div className={`lp-alert lp-alert-${type}`}>
      {type === 'error'
        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
      {msg}
    </div>
  );
}

/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
   MAIN PAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export default function LoginPage() {
  const navigate = useNavigate();
  const panInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const { brandName, logoUrl, logoAlt } = useBrand();

  /* shared */
  const [pan, setPan]     = useState('');
  const [phone, setPhone] = useState('');
  const [terms, setTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState('');

  /* OTP */
  const [otpDigits, setOtpDigits] = useState(['','','','']);
  const [showOtp, setShowOtp]     = useState(false);
  const [resendT, setResendT]     = useState(0);
  const [expiryT, setExpiryT]     = useState(0);
  const [canResend, setCanResend] = useState(false);

  const panStatus = pan.length === 0 ? 'empty' : isValidPAN(pan) ? 'valid' : pan.length < 10 ? 'typing' : 'invalid';

  useEffect(() => { setError(''); setSuccess(''); }, [pan, phone]);

  /* countdown timers */
  useEffect(() => {
    if (resendT <= 0) { if (showOtp) setCanResend(true); return; }
    const t = setTimeout(() => setResendT(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendT, showOtp]);

  useEffect(() => {
    if (!showOtp || expiryT <= 0) return;
    const t = setTimeout(() => setExpiryT(v => v - 1), 1000);
    return () => clearTimeout(t);
  }, [expiryT, showOtp]);

  useEffect(() => {
    if (showOtp && expiryT === 0) {
      setError('OTP expired. Please request a new one.');
      setOtpDigits(['','','','']);
      setResendT(0);
      setCanResend(true);
    }
  }, [expiryT, showOtp]);

  /* helpers */
  const post = async (path, body) => {
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const raw = await res.text();
    let data = {};

    try {
      data = raw ? JSON.parse(raw) : {};
    } catch {
      data = {
        success: false,
        message: friendlyErrorMessage(res.status, raw),
      };
    }

    if (!res.ok && data?.message) {
      data.message = friendlyErrorMessage(res.status, data.message);
    }

    return { ok: res.ok, status: res.status, data };
  };

  // docsRequired lists the documents the AI agent is still waiting on; the
  // customer lands on the Documents page until they are uploaded.
  const saveSession = (user, token, isNewUser = false, hasLoanTaken = false, docsRequired = [], docsPendingCount) => {
    const pendingDocs = Number.isFinite(Number(docsPendingCount))
      ? Number(docsPendingCount)
      : (Array.isArray(docsRequired) ? docsRequired.length : 0);
    const session = {
      userId: user.id, phone: user.phone, pan: user.pan_number,
      name: user.name, role: user.role, loginTime: Date.now(),
      expiresAt: Date.now() + 30 * 60 * 1000,
      clientId: user.client_id,
      leadId: user.leadId ?? null,
      token: token,
      isNewUser: Boolean(isNewUser),
      hasLoanTaken: Boolean(hasLoanTaken),
      // Outstanding document groups; the sidebar shows this as a badge.
      docsPendingCount: pendingDocs,
    };
    localStorage.setItem('userSession', JSON.stringify(session));
    window.dispatchEvent(new CustomEvent('userSessionChanged', {
      detail: session
    }));
    navigate(docsRequired?.length ? '/dashboard/documents' : '/dashboard/apply-loan', { replace: true });
  };

  const startOtp = (expiresInSeconds = 600) => {
    setShowOtp(true); setResendT(60);
    setExpiryT(Number(expiresInSeconds) || 600);
    setCanResend(false); setOtpDigits(['','','','']);
  };

  const handleOtpKey = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const n = [...otpDigits]; n[i] = val; setOtpDigits(n);
    if (val && i < 3) document.getElementById(`otp-${i+1}`)?.focus();
  };

  /* ── Unified: Request OTP ── */
  const handleRequestOtp = async e => {
    if (e?.preventDefault) e.preventDefault();
    if (!isValidPAN(pan)) return setError('Enter a valid PAN (e.g. ABCDE1234F)');
    if (phone.length !== 10) return setError('Enter a valid 10-digit mobile number');
    if (!terms) return setError('Please accept Terms, Privacy Policy and Bureau Consent');
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { ok, data } = await post('/api/auth/request-otp', { panNumber: pan, phone, consentAccepted: terms, debug: true });
      if (ok && data.success) { startOtp(data.expiresIn); setSuccess('OTP sent!'); setTimeout(() => setSuccess(''), 3000); }
      else setError(data.message || 'Failed to send OTP');
    } catch { setError('Unable to reach the server. Please try again.'); }
    finally { setLoading(false); }
  };

  /* ── Unified: Verify OTP ── */
  const handleVerifyOtp = async e => {
    e.preventDefault();
    const code = otpDigits.join('');
    if (code.length !== 4) return;
    setLoading(true);
    try {
      const { ok, data } = await post('/api/auth/verify-otp', { panNumber: pan, phone, otp: code, consentAccepted: terms, debug: true });
      if (ok && data.success) {
         if (data.message) setSuccess(data.message);
         if (data.isNewUser) {
             setTimeout(() => saveSession(data.user, data.token, true, data.hasLoanTaken, data.docsRequired, data.docsPendingCount), 1200);
         } else {
             saveSession(data.user, data.token, false, data.hasLoanTaken, data.docsRequired, data.docsPendingCount);
         }
      } else {
         setError(data.message || 'Invalid OTP');
         if (data.message && data.message.toLowerCase().includes('incorrect pan')) {
            setTimeout(() => setError(''), 2000);
         }
      }
    } catch { setError('Unable to reach the server. Please try again.'); }
    finally { setLoading(false); }
  };

  const isFormReady = isValidPAN(pan) && phone.length === 10 && terms;

  /* ---- render helpers (plain functions, NOT components — avoids remount on re-render) ---- */
  const renderPanField = () => (
    <div className={`lp-field ${['typing', 'invalid'].includes(panStatus) ? panStatus : ''}`}>
      <label>PAN Number</label>
      <div className="lp-input-wrap" onMouseDown={e => e.target.tagName !== 'INPUT' && panInputRef.current?.focus()}>
        <span className="lp-input-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 14H4v-6h16zm0-10H4V6h16z"/></svg>
        </span>
        <input ref={panInputRef} id="pan-f" type="text" placeholder="ABCDE1234F" value={pan} maxLength={10}
          onChange={e => setPan(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,''))}
          autoComplete="off" spellCheck={false}
          style={{ textTransform:'uppercase', letterSpacing:'0.12em', fontWeight:600 }} />
        {panStatus === 'valid' && <CheckIcon />}
      </div>
      {pan.length > 0 && pan.length < 10 && <span className="lp-hint">{10 - pan.length} more chars</span>}
      {panStatus === 'invalid' && <span className="lp-hint err">Invalid PAN format</span>}
    </div>
  );

  const renderPhoneField = () => (
    <div className="lp-field">
      <label>Mobile Number</label>
      <div className="lp-input-wrap" onMouseDown={e => e.target.tagName !== 'INPUT' && phoneInputRef.current?.focus()}>
        <span className="lp-input-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
        </span>
        <span className="lp-input-code">+91</span>
        <input ref={phoneInputRef} id="ph-f" type="tel" placeholder="Enter mobile linked with PAN & Aadhaar" value={phone} maxLength={10}
          aria-describedby="ph-f-hint"
          onChange={e => setPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
          />
        {phone.length === 10 && <CheckIcon />}
      </div>
      <span id="ph-f-hint" className="lp-hint err">
        Enter the 10-digit mobile number linked with your PAN and Aadhaar.
      </span>
    </div>
  );

  const renderTermsRow = () => (
    <div className="lp-check-row">
      <label className="lp-checkbox">
        <input type="checkbox" checked={terms} onChange={e => setTerms(e.target.checked)} />
        <span className="lp-check-box" />
        <span className="lp-checkbox-text">
          I agree to the <Link to="/privacy">Terms</Link>, <Link to="/privacy">Privacy Policy</Link> and Bureau Consent for credit report pull.
        </span>
      </label>
    </div>
  );

  const renderBtnPrimary = (label, disabled, showArrow = true) => (
    <button type="submit" className={`lp-btn-primary ${loading ? 'is-loading' : ''}`} disabled={disabled || loading}>
      {loading
        ? <span className="lp-spinner" />
        : <>
            {disabled && <svg className="lp-btn-lock" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
            {label}
          </>}
      {!loading && !disabled && showArrow && <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>}
    </button>
  );

  const renderOtpBoxes = (onSubmit) => (
    <form className="lp-form" onSubmit={onSubmit}>
	      <div className="lp-field">
	        <label>Enter 4-digit OTP</label>
	        <div className="lp-otp-row">
	          {otpDigits.map((d, i) => (
            <input key={i} id={`otp-${i}`} className="lp-otp-box"
              type="text" inputMode="numeric" maxLength={1} value={d}
              onChange={e => handleOtpKey(i, e.target.value)}
              onKeyDown={e => e.key === 'Backspace' && !d && i > 0 && document.getElementById(`otp-${i-1}`)?.focus()}
              autoFocus={i === 0} />
          ))}
	        </div>
	        <div className="lp-otp-meta">
	          {resendT > 0
	            ? <span className="lp-resend-wait">Resend in {resendT}s</span>
            : <button type="button" className="lp-resend-btn" onClick={handleRequestOtp} disabled={!canResend || loading}>Resend OTP</button>}
        </div>
      </div>
      {renderBtnPrimary('Verify & Continue', otpDigits.join('').length !== 4)}
      <a href="/login" className="lp-login-link" onClick={e => { e.preventDefault(); setShowOtp(false); setOtpDigits(['','','','']); setError(''); setSuccess(''); navigate('/login'); }}>
        ← Login
      </a>
    </form>
  );

  const otpSentTo = (
    <>
      We sent a 4-digit verification code to{' '}
      <strong>
        +91&nbsp;
        {phone.slice(0, 2)}
        ••••••
        {phone.slice(-2)}
      </strong>
    </>
  );

  /* ━━━━━━━━━━━━━━━━━━━━━━ RENDER ━━━━━━━━━━━━━━━━━━━━━━ */
return (
  <div className="lp-root">

    {/* ===== Desktop scenery (hidden below 1024px) ===== */}
    <div className="lp-desk-bg">
      <span className="lpd-blob lpd-blob-tl" />
      <span className="lpd-blob lpd-blob-br" />
    </div>

    <div className="lp-secure">
      <span className="lp-secure-dot" />
      Secure connection
    </div>

    <div className="lp-layout">

    {/* ===== Desktop side column (hidden below 1024px) ===== */}
    <section className="lp-side">

      <div className="lpd-brand">
        <img src={logoUrl} alt={logoAlt || brandName} />
        <div>
          <strong>{brandName}</strong>
          <span>Secure Today, Smarter Tomorrow.</span>
        </div>
      </div>

      <h1>
        Secure Your Loans.
        <span>Build a Better Tomorrow.</span>
      </h1>

      <p className="lpd-lead">
        We provide secure, fast and intelligent lending
        solutions to help you achieve your financial goals.
      </p>

      <ul className="lpd-perks">
        {HERO_PERKS.map(perk => (
          <li key={perk.title}>
            <span className={`lpd-perk-icon is-${perk.tone}`}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                {perk.icon}
              </svg>
            </span>
            <div>
              <strong>{perk.title}</strong>
              <small>{perk.text}</small>
            </div>
          </li>
        ))}
      </ul>

      <LoginArt />

      <div className="lpd-tagline">
        SAFE · SIMPLE · DIGITAL LENDING
      </div>

    </section>

    <main className="lp-frame">

      {/* ===== Blue header ===== */}
      <section className="lp-top">

        <div className="lp-brand">
          <img src={logoUrl} alt={logoAlt || brandName} />
          <div>
            <strong>{brandName}</strong>
            <span>Secure Today, Smarter Tomorrow.</span>
          </div>
        </div>

        <div className="lp-intro">
          <div className="lp-copy">
            <h1>
              {showOtp ? 'Verify It\'s You' : <>Welcome Back <span className="lp-wave" aria-hidden="true">👋</span></>}
            </h1>
            <p>
              {showOtp
                ? 'Enter the code we sent to your mobile number.'
                : 'Login to continue your secure journey.'}
            </p>
          </div>

          <ShieldArt />
        </div>

      </section>


      {/* ===== Form sheet ===== */}
      <section className="lp-sheet">

        {/* desktop card heading (the blue header does this job on phones) */}
        <div className="lp-desk-head">
          <div className="lpd-brand">
            <img src={logoUrl} alt={logoAlt || brandName} />
            <div>
              <strong>{brandName}</strong>
              <span>Secure Today, Smarter Tomorrow.</span>
            </div>
          </div>
          <h2>{showOtp ? 'Verify your number' : 'Welcome Back'}</h2>
          <p className="lp-desk-sub">
            {showOtp ? otpSentTo : 'Login to your account to continue your loan journey.'}
          </p>
        </div>

        {showOtp && (
          <div className="lp-sheet-head">
            <h2>Verify your number</h2>
            <p className="lp-sheet-sub">{otpSentTo}</p>
          </div>
        )}

        <Alert
          type="error"
          msg={error}
        />

        <Alert
          type="success"
          msg={success}
        />

        {!showOtp ? (

          <form
            className="lp-form"
            onSubmit={handleRequestOtp}
          >

            {renderPanField()}

            {renderPhoneField()}

            {renderTermsRow()}

            {renderBtnPrimary(
              'Get OTP',
              !isFormReady
            )}

            <TrustStrip />

          </form>

        ) : (

          renderOtpBoxes(handleVerifyOtp)

        )}

        <p className="lp-legal">
          <span>
            RBI Registration No: B-14.01639
          </span>
          <br />
          <span>
            © 2026 Richman Fincap Ltd. All rights reserved.
          </span>
        </p>

      </section>

    </main>

    </div>

  </div>
);
}

